import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Header,
  Button,
  Icon,
  Segment,
  Modal,
  Form,
  Dropdown,
  Divider,
  Container,
  Grid,
  Message,
  Input,
  Label,
  Loader,
} from "semantic-ui-react";

import { faqsApi } from "@api/faqs";
import { authenticationService } from "@authentication/services/AuthenticationService";

const DEFAULT_CATEGORIES = [
  "General",
  "Account Management",
  "Library Services",
  "Digital Resources",
];

const FrequentlyAskedQuestions = ({
  user,
  isAnonymous,
  sendErrorNotification,
  sendSuccessNotification,
  sendWarningNotification,
}) => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [activeDropdown, setActiveDropdown] = useState(null);

  const [showPostModal, setShowPostModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", content: "" });

  const [editingFaq, setEditingFaq] = useState(null);
  const [deletingFaq, setDeletingFaq] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    category: "",
  });

  const hasManagementAccess = () => {
    if (isAnonymous || !user) return false;
    return authenticationService.hasRoles(user, ["admin", "librarian"]);
  };

  useEffect(() => {
    if (faqs.length > 0) {
      const faqCategories = [
        ...new Set(faqs.map((faq) => faq.category).filter(Boolean)),
      ];
      if (faqCategories.length > 0) {
        setCategories(faqCategories);
      } else {
        setCategories(DEFAULT_CATEGORIES);
      }
    } else if (categories.length === 0) {
      setCategories(DEFAULT_CATEGORIES);
    }
  }, [faqs]);

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("[data-dropdown]") &&
        !event.target.closest(".ellipsis-icon")
      ) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const response = await faqsApi.list();

      let fetchedFaqs = [];
      if (response.data) {
        if (response.data.data) {
          fetchedFaqs = response.data.data;
        } else if (
          response.data.hits &&
          Array.isArray(response.data.hits) &&
          response.data.hits.length > 0 &&
          response.data.hits[0].question
        ) {
          fetchedFaqs = response.data.hits;
        } else if (Array.isArray(response.data)) {
          fetchedFaqs = response.data;
        }
      }

      const mappedFaqs = fetchedFaqs.map((faq, index) => ({
        ...faq,
        id: faq.id || faq._id || `temp_${index}`,
        question: faq.question || "",
        answer: faq.answer || "",
        category: faq.category || "General",
      }));

      setFaqs(mappedFaqs);
    } catch (error) {
      showMessage("error", "Failed to load FAQs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, content) => {
    if (type === "success") {
      sendSuccessNotification("Success!", content);
    } else if (type === "error") {
      sendErrorNotification("Error", content);
    } else if (type === "warning") {
      sendWarningNotification("Warning", content);
    }

    setMessage({ type, content });
    setTimeout(() => setMessage({ type: "", content: "" }), 5000);
  };

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedFaqs = filteredFaqs.reduce((acc, faq) => {
    const category = faq.category || "Uncategorized";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {});

  const categoryOptions = categories.map((cat) => ({
    key: cat,
    text: cat,
    value: cat,
  }));

  const handleToggleExpand = (faqId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedItems(newExpanded);
  };

  const handleDropdownToggle = (faqId, event) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === faqId ? null : faqId);
  };

  const handlePostNew = () => {
    setEditingFaq(null);
    setFormData({
      question: "",
      answer: "",
      category: categories[0] || DEFAULT_CATEGORIES[0],
    });
    setShowPostModal(true);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question || "",
      answer: faq.answer || "",
      category: faq.category || categories[0] || DEFAULT_CATEGORIES[0],
    });
    setShowPostModal(true);
    setActiveDropdown(null);
  };

  const handleDelete = (faq) => {
    setDeletingFaq(faq);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleSubmit = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      showMessage("error", "Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      if (editingFaq) {
        await faqsApi.update(editingFaq.id, formData);

        const updatedFaq = {
          ...editingFaq,
          ...formData,
          id: editingFaq.id,
        };

        setFaqs((prevFaqs) =>
          prevFaqs.map((faq) => (faq.id === editingFaq.id ? updatedFaq : faq))
        );

        showMessage("success", "FAQ updated successfully!");
      } else {
        const response = await faqsApi.create(formData);

        const newFaq = {
          ...formData,
          id: response.data?.id || response.data?._id || `new_${Date.now()}`,
          question: response.data?.question || formData.question,
          answer: response.data?.answer || formData.answer,
          category: response.data?.category || formData.category,
        };

        setFaqs((prevFaqs) => [...prevFaqs, newFaq]);

        if (!categories.includes(formData.category)) {
          setCategories((prev) => [...prev, formData.category]);
        }

        showMessage("success", "FAQ created successfully!");
      }

      setShowPostModal(false);
      setFormData({
        question: "",
        answer: "",
        category: categories[0] || DEFAULT_CATEGORIES[0],
      });
      setEditingFaq(null);
    } catch (error) {
      showMessage(
        "error",
        `Failed to ${editingFaq ? "update" : "create"} FAQ. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingFaq) return;

    setSubmitting(true);
    try {
      await faqsApi.delete(deletingFaq.id);
      setFaqs(faqs.filter((faq) => faq.id !== deletingFaq.id));
      showMessage("success", "FAQ deleted successfully!");
      setShowDeleteModal(false);
      setDeletingFaq(null);
    } catch (error) {
      showMessage("error", "Failed to delete FAQ. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddCategory = () => {
    const trimmedCategory = newCategory.trim();
    if (!trimmedCategory) {
      showMessage("error", "Please enter a category name.");
      return;
    }

    if (categories.includes(trimmedCategory)) {
      showMessage("error", "This category already exists.");
      return;
    }

    setCategories((prev) => [...prev, trimmedCategory]);
    setNewCategory("");
    showMessage("success", `Category "${trimmedCategory}" added successfully!`);
  };

  const handleRemoveCategory = (categoryToRemove) => {
    if (categories.length <= 1) {
      showMessage("error", "Cannot remove the last category.");
      return;
    }

    const faqsUsingCategory = faqs.filter(
      (faq) => faq.category === categoryToRemove
    );

    if (faqsUsingCategory.length > 0) {
      showMessage(
        "error",
        `Cannot remove category "${categoryToRemove}" because it contains ${
          faqsUsingCategory.length
        } FAQ${
          faqsUsingCategory.length !== 1 ? "s" : ""
        }. Please reassign or delete those FAQs first.`
      );
      return;
    }

    const remainingCategories = categories.filter(
      (cat) => cat !== categoryToRemove
    );
    setCategories(remainingCategories);
    showMessage(
      "success",
      `Category "${categoryToRemove}" removed successfully!`
    );
  };

  if (loading) {
    return (
      <Container fluid style={{ padding: "2rem" }}>
        <Loader active inline="centered">
          Loading FAQs...
        </Loader>
      </Container>
    );
  }

  return (
    <div style={{ width: "100%", minHeight: "100vh" }}>
      <Container fluid style={{ padding: "2rem" }}>
        {message.content && (
          <Message
            positive={message.type === "success"}
            negative={message.type === "error"}
            warning={message.type === "warning"}
            info={message.type === "info"}
            onDismiss={() => setMessage({ type: "", content: "" })}
            style={{ marginBottom: "1rem" }}
          >
            <Message.Header>
              {message.type === "success"
                ? "Success!"
                : message.type === "error"
                ? "Error"
                : message.type === "warning"
                ? "Warning"
                : "Info"}
            </Message.Header>
            {message.content}
          </Message>
        )}

        <Header as="h2">
          <Header.Content>
            Frequently Asked Questions
            <Header.Subheader className="fs-subheader">
              Manage and organize your FAQ content
            </Header.Subheader>
          </Header.Content>
        </Header>

        <Grid>
          <Grid.Row>
            <Grid.Column width={10} textAlign="center">
              <Input
                icon="search"
                iconPosition="left"
                placeholder="Search FAQs..."
                fluid
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              {hasManagementAccess() && (
                <Button.Group>
                  <Button
                    icon
                    labelPosition="left"
                    onClick={() => setShowCategoryModal(true)}
                    size="small"
                  >
                    <Icon name="tags" />
                    Manage Categories
                  </Button>
                  <Button
                    primary
                    icon
                    labelPosition="left"
                    onClick={handlePostNew}
                    size="small"
                  >
                    <Icon name="plus" />
                    Add FAQ
                  </Button>
                </Button.Group>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {searchTerm && (
          <Message info style={{ marginBottom: "1rem" }}>
            <Message.Header>Search Results</Message.Header>
            {filteredFaqs.length > 0 ? (
              `Found ${filteredFaqs.length} FAQ${
                filteredFaqs.length !== 1 ? "s" : ""
              } matching "${searchTerm}"`
            ) : (
              <>
                No FAQs found matching "<strong>{searchTerm}</strong>"
                <div style={{ marginTop: "0.5rem", fontSize: "0.9em" }}>
                  <strong>Try:</strong>
                  <ul style={{ marginTop: "0.25rem", marginBottom: 0 }}>
                    <li>Using different keywords</li>
                    <li>Checking your spelling</li>
                    <li>Using more general terms</li>
                    <li>Browsing categories below</li>
                  </ul>
                </div>
              </>
            )}
          </Message>
        )}

        <div style={{ marginTop: "2rem" }}>
          {Object.keys(groupedFaqs).length === 0 ? (
            <Message warning style={{ textAlign: "center" }}>
              {searchTerm ? (
                <>
                  <Message.Header>
                    <Icon name="search" />
                    No Results Found
                  </Message.Header>
                  <p>
                    We couldn't find any FAQs matching "
                    <strong>{searchTerm}</strong>".
                  </p>
                  <div style={{ marginTop: "1rem" }}>
                    <Button
                      primary
                      size="small"
                      onClick={() => setSearchTerm("")}
                    >
                      <Icon name="refresh" />
                      Clear Search & View All
                    </Button>
                  </div>
                  {hasManagementAccess() && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <Button
                        color="green"
                        size="small"
                        onClick={handlePostNew}
                      >
                        <Icon name="plus" />
                        Add New FAQ
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Message.Header>
                    <Icon name="help circle" />
                    No FAQs Available
                  </Message.Header>
                  <p>There are no frequently asked questions added yet.</p>
                  {hasManagementAccess() ? (
                    <div style={{ marginTop: "1rem" }}>
                      <Button primary size="medium" onClick={handlePostNew}>
                        <Icon name="plus" />
                        Add Your First FAQ
                      </Button>
                    </div>
                  ) : (
                    <p style={{ color: "#666", fontSize: "0.9em" }}>
                      Please check back later or contact the library for
                      assistance.
                    </p>
                  )}
                </>
              )}
            </Message>
          ) : (
            Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
              <Segment key={category} style={{ marginBottom: "1.5rem" }}>
                <Header
                  as="h3"
                  style={{ marginBottom: "1rem", color: "#2185d0" }}
                >
                  {category}
                </Header>
                <Divider style={{ margin: "0 0 1rem 0" }} />
                {categoryFaqs.map((faq, index) => (
                  <div
                    key={faq.id}
                    style={{
                      borderBottom:
                        index < categoryFaqs.length - 1
                          ? "1px solid #e0e0e0"
                          : "none",
                      padding: "0.75rem 0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Icon
                        name={
                          expandedItems.has(faq.id)
                            ? "chevron down"
                            : "chevron right"
                        }
                        style={{
                          cursor: "pointer",
                          fontSize: "0.9em",
                          color: "#666",
                          margin: 0,
                          minWidth: "16px",
                        }}
                        onClick={() => handleToggleExpand(faq.id)}
                      />

                      <div
                        style={{
                          flex: 1,
                          cursor: "pointer",
                          padding: "0.25rem 0",
                        }}
                        onClick={() => handleToggleExpand(faq.id)}
                      >
                        <Header
                          as="h4"
                          style={{
                            margin: 0,
                            fontSize: "1.1em",
                            fontWeight: "500",
                            color: "#333",
                          }}
                        >
                          {faq.question}
                        </Header>
                      </div>

                      {hasManagementAccess() && (
                        <Icon
                          name="ellipsis horizontal"
                          className="ellipsis-icon"
                          style={{
                            cursor: "pointer",
                            fontSize: "1.1em",
                            color: "#666",
                            padding: "0.25rem",
                            borderRadius: "3px",
                            minWidth: "20px",
                            textAlign: "center",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDropdownToggle(faq.id, e);
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#f5f5f5";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "transparent";
                          }}
                        />
                      )}

                      {hasManagementAccess() && activeDropdown === faq.id && (
                        <div style={{ position: "relative" }} data-dropdown>
                          <div
                            style={{
                              position: "absolute",
                              top: "25px",
                              right: 0,
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              zIndex: 1000,
                              minWidth: "120px",
                            }}
                          >
                            <div
                              style={{
                                padding: "0.5rem 0.75rem",
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                                fontSize: "0.9em",
                              }}
                              onClick={() => handleEdit(faq)}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#f8f9fa";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "white";
                              }}
                            >
                              <Icon
                                name="edit"
                                style={{
                                  marginRight: "0.5rem",
                                  color: "#2185d0",
                                }}
                              />
                              Edit
                            </div>
                            <div
                              style={{
                                padding: "0.5rem 0.75rem",
                                cursor: "pointer",
                                fontSize: "0.9em",
                              }}
                              onClick={() => handleDelete(faq)}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#fff5f5";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "white";
                              }}
                            >
                              <Icon
                                name="trash"
                                style={{
                                  marginRight: "0.5rem",
                                  color: "#db2828",
                                }}
                              />
                              Delete
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {expandedItems.has(faq.id) && (
                      <div
                        style={{
                          marginTop: "0.75rem",
                          marginLeft: "1.5rem",
                          paddingLeft: "1rem",
                          borderLeft: "3px solid #e8f4fd",
                          backgroundColor: "#fafbfc",
                        }}
                      >
                        <div
                          style={{
                            padding: "1rem",
                            lineHeight: "1.6",
                            color: "#555",
                            fontSize: "0.95em",
                          }}
                        >
                          {faq.answer}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </Segment>
            ))
          )}
        </div>

        {hasManagementAccess() && (
          <Modal
            open={showCategoryModal}
            onClose={() => setShowCategoryModal(false)}
            size="small"
          >
            <Header icon="tags" content="Manage Categories" />
            <Modal.Content>
              <Form>
                <Form.Field>
                  <label>Add New Category</label>
                  <Form.Input
                    placeholder="Enter category name..."
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddCategory()}
                    action={{
                      color: "blue",
                      icon: "plus",
                      content: "Add",
                      onClick: handleAddCategory,
                      disabled:
                        !newCategory.trim() ||
                        categories.includes(newCategory.trim()),
                    }}
                  />
                </Form.Field>
              </Form>

              <Divider />

              <Header as="h4">Existing Categories</Header>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {categories.map((category) => {
                  const faqCount = faqs.filter(
                    (faq) => faq.category === category
                  ).length;
                  const canRemove = faqCount === 0 && categories.length > 1;

                  return (
                    <div key={category} style={{ position: "relative" }}>
                      <Label
                        color="blue"
                        style={{ paddingRight: canRemove ? "2rem" : undefined }}
                      >
                        {category}
                      </Label>
                      {canRemove && (
                        <Icon
                          name="times"
                          style={{
                            position: "absolute",
                            right: "0.5rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#fff",
                            fontSize: "0.8em",
                          }}
                          onClick={() => handleRemoveCategory(category)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </Modal.Content>

            <Modal.Actions>
              <Button
                onClick={() => {
                  setShowCategoryModal(false);
                  setNewCategory("");
                  setMessage({ type: "", content: "" });
                }}
              >
                <Icon name="remove" /> Close
              </Button>
            </Modal.Actions>
          </Modal>
        )}

        {hasManagementAccess() && (
          <Modal
            open={showPostModal}
            onClose={() => setShowPostModal(false)}
            size="small"
          >
            <Header
              as="h4"
              icon="edit"
              content={editingFaq ? "Edit FAQ" : "Add New FAQ"}
            />
            <Modal.Content>
              <Form loading={submitting}>
                <Form.Field required>
                  <label>Category</label>
                  <Dropdown
                    placeholder="Select Category"
                    fluid
                    selection
                    options={categoryOptions}
                    value={formData.category}
                    onChange={(e, { value }) =>
                      setFormData({ ...formData, category: value })
                    }
                    style={{ color: "#333" }}
                  />
                </Form.Field>
                <Form.Field required>
                  <label>Question</label>
                  <Form.Input
                    placeholder="Enter question here..."
                    value={formData.question}
                    onChange={(e) =>
                      setFormData({ ...formData, question: e.target.value })
                    }
                  />
                </Form.Field>
                <Form.Field required>
                  <label>Answer</label>
                  <Form.TextArea
                    placeholder="Enter answer here..."
                    value={formData.answer}
                    onChange={(e) =>
                      setFormData({ ...formData, answer: e.target.value })
                    }
                    rows={6}
                  />
                </Form.Field>
              </Form>
            </Modal.Content>

            <Modal.Actions>
              <Button
                onClick={() => setShowPostModal(false)}
                disabled={submitting}
              >
                <Icon name="remove" /> Cancel
              </Button>
              <Button
                primary
                onClick={handleSubmit}
                disabled={
                  !formData.question.trim() ||
                  !formData.answer.trim() ||
                  submitting
                }
                loading={submitting}
              >
                <Icon name="checkmark" />
                {editingFaq ? "Update FAQ" : "Add FAQ"}
              </Button>
            </Modal.Actions>
          </Modal>
        )}

        {hasManagementAccess() && (
          <Modal
            open={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            size="tiny"
          >
            <Header icon="trash alternate outline" content="Confirm Deletion" />
            <Modal.Content>
              <p>
                Are you sure you want to delete this FAQ? This action cannot be
                undone.
              </p>
              {deletingFaq && (
                <Message>
                  <strong>Question:</strong> {deletingFaq.question}
                </Message>
              )}
            </Modal.Content>
            <Modal.Actions>
              <Button
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
              >
                <Icon name="remove" /> Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={submitting}
                disabled={submitting}
              >
                <Icon name="trash" /> Delete
              </Button>
            </Modal.Actions>
          </Modal>
        )}
      </Container>
    </div>
  );
};

FrequentlyAskedQuestions.propTypes = {
  user: PropTypes.object,
  isAnonymous: PropTypes.bool,
  sendErrorNotification: PropTypes.func.isRequired,
  sendSuccessNotification: PropTypes.func.isRequired,
  sendWarningNotification: PropTypes.func.isRequired,
};

FrequentlyAskedQuestions.defaultProps = {
  user: null,
  isAnonymous: true,
};

export default FrequentlyAskedQuestions;
